import type { GardenUseKey, PublicPlan, QuestionnaireData } from '@data/publicPlans';
import type { ShareVisibility } from '@utils/shareVisibility';
import { normalizePricingBreakdown } from '@utils/pricingBreakdownUtils';

/**
 * Convert a row from GET /plans or GET /companies/my/invite-plans into {@link PublicPlan}.
 */
export function mapApiPlanToPublicPlan(apiPlan: unknown): PublicPlan {
	const p = apiPlan as Record<string, unknown>;
	const decodeJsonMaybe = (val: unknown): any | undefined => {
		if (val == null) return undefined;
		if (typeof val === 'string') {
			const trimmed = val.trim();
			if (!trimmed) return undefined;
			try {
				return JSON.parse(trimmed);
			} catch {
				return undefined;
			}
		}
		if (typeof val === 'object') return val;
		return undefined;
	};
	let aiDetails = (p.ai_plan_details || {}) as Record<string, unknown>;
	if (typeof aiDetails === 'string') {
		try {
			aiDetails = JSON.parse(aiDetails) as Record<string, unknown>;
		} catch {
			aiDetails = {};
		}
	}
	const aiGenerated =
		(aiDetails.aiGenerated as PublicPlan['aiGenerated']) ||
		(aiDetails.photoAfterUrl ? (aiDetails as PublicPlan['aiGenerated']) : undefined);

	let selectedComponents: string[] | undefined;
	if (p.garden_components) {
		try {
			const parsed =
				typeof p.garden_components === 'string' ? JSON.parse(p.garden_components as string) : p.garden_components;
			selectedComponents = Array.isArray(parsed) ? parsed : undefined;
		} catch {
			/* ignore */
		}
	}

	const hasQuestionnaireData = Boolean(
		p.garden_size !== null && p.garden_size !== undefined ||
			p.before_image_url ||
			p.style ||
			p.location ||
			p.needs_wants ||
			p.timeline ||
			p.project_start_date ||
			p.garden_shape ||
			p.garden_components ||
			p.project_focus ||
			p.garden_uses ||
			p.ai_answers_json ||
			p.ai_questions_json ||
			p.ai_analysis_json ||
			p.ai_answers ||
			p.ai_questions ||
			p.ai_analysis
	);

	let gardenUses: GardenUseKey[] | undefined;
	if (p.garden_uses) {
		try {
			const parsed = typeof p.garden_uses === 'string' ? JSON.parse(p.garden_uses as string) : p.garden_uses;
			gardenUses = Array.isArray(parsed) ? (parsed as GardenUseKey[]) : undefined;
		} catch {
			gardenUses = undefined;
		}
	}

	const aiQuestionsRaw = p.ai_questions_json ?? p.ai_questions;
	const aiAnswersRaw = p.ai_answers_json ?? p.ai_answers;
	const aiAnalysisRaw = p.ai_analysis_json ?? p.ai_analysis;

	const aiQuestionsDecoded = decodeJsonMaybe(aiQuestionsRaw);
	const aiAnswersDecoded = decodeJsonMaybe(aiAnswersRaw);
	const aiAnalysisDecoded = decodeJsonMaybe(aiAnalysisRaw);

	const aiQuestionComments: Record<string, string> = {};
	let aiAnswersClean: Record<string, any> | undefined;
	if (aiAnswersDecoded && typeof aiAnswersDecoded === 'object' && !Array.isArray(aiAnswersDecoded)) {
		aiAnswersClean = {};
		for (const [k, v] of Object.entries(aiAnswersDecoded as Record<string, any>)) {
			const match = k.match(/^ai_q_(\d+)_comment$/);
			if (match) {
				aiQuestionComments[match[1]] = v == null ? '' : String(v);
			} else {
				aiAnswersClean[k] = v;
			}
		}
	}

	const questionnaireAiAnalysis = (() => {
		if (!aiAnalysisDecoded && !aiQuestionsDecoded) return undefined;
		const base = (aiAnalysisDecoded && typeof aiAnalysisDecoded === 'object' ? aiAnalysisDecoded : {}) as Record<string, any>;
		const coerceQuestionsToArray = (decoded: any): any[] | undefined => {
			if (!decoded) return undefined;
			if (Array.isArray(decoded)) return decoded;
			if (decoded && typeof decoded === 'object') {
				if (Array.isArray(decoded.suggestedQuestions)) return decoded.suggestedQuestions;
				const entries = Object.entries(decoded);
				const numericEntries = entries.filter(([k]) => /^-?\d+$/.test(k));
				if (numericEntries.length > 0) {
					return numericEntries
						.sort(([a], [b]) => Number(a) - Number(b))
						.map(([, v]) => v)
						.filter(Boolean);
				}
			}
			return undefined;
		};
		const suggestedQuestionsFromAiQuestions = coerceQuestionsToArray(aiQuestionsDecoded);
		const suggestedQuestionsFromBase = coerceQuestionsToArray(base?.suggestedQuestions);
		return {
			...base,
			suggestedQuestions: suggestedQuestionsFromAiQuestions ?? suggestedQuestionsFromBase
		};
	})();

	const questionnaire = hasQuestionnaireData
		? ({
				gardenSize: (p.garden_size as number) || 0,
				gardenShape: (p.garden_shape as QuestionnaireData['gardenShape']) || undefined,
				selectedComponents,
				photoBeforeUrl: (p.before_image_url as string) || undefined,
				styleKey: (p.style as QuestionnaireData['styleKey']) || 'modern',
				needsWants: (p.needs_wants as string) || '',
				maintenanceLevel: (p.maintenance_level as QuestionnaireData['maintenanceLevel']) || undefined,
				childrenPets: (p.children_pets as QuestionnaireData['childrenPets']) || undefined,
				budgetRange:
					p.budget_min !== null &&
					p.budget_min !== undefined &&
					p.budget_max !== null &&
					p.budget_max !== undefined
						? {
								min: p.budget_min as number,
								max: p.budget_max as number
							}
						: undefined,
				timeline: (p.timeline as string) || '',
				projectStartDate: (p.project_start_date as string) || undefined,
				location: (p.location as string) || '',
				householdSize: p.household_size as number | undefined,
				gardenUsers: p.garden_users as number | undefined,
				plantDensityPreference: p.plant_density_preference as QuestionnaireData['plantDensityPreference'] | undefined,
				projectFocus: p.project_focus as QuestionnaireData['projectFocus'] | undefined,
				gardenUses: gardenUses ?? [],
				hostingMaxPeople: p.hosting_max_people as number | undefined,
				kidsCount: p.kids_count as number | undefined,
				kidsAges: (p.kids_ages as string) || undefined,
				petsDescription: (p.pets_description as string) || undefined,
				aiAnalysis: questionnaireAiAnalysis as QuestionnaireData['aiAnalysis'],
				aiAnswers: aiAnswersClean,
				aiQuestionComments: Object.keys(aiQuestionComments).length > 0 ? aiQuestionComments : undefined
			} as QuestionnaireData)
		: undefined;

	let finalAiGenerated = aiGenerated;
	const afterUrl = p.after_image_url as string | undefined;
	if (afterUrl) {
		if (aiGenerated) {
			finalAiGenerated = {
				...aiGenerated,
				photoAfterUrl: aiGenerated.photoAfterUrl || afterUrl
			};
		} else {
			finalAiGenerated = {
				photoAfterUrl: afterUrl,
				planDescription: (p.description as string) || '',
				keyFeatures: [],
				instructions: [],
				pricingBreakdown: {
					min: 0,
					mid: 0,
					max: 0,
					materials: [],
					labor: []
				},
				generatedAt: p.created_at as string
			} as PublicPlan['aiGenerated'];
		}
	}

	if (finalAiGenerated?.pricingBreakdown) {
		finalAiGenerated = {
			...finalAiGenerated,
			pricingBreakdown: normalizePricingBreakdown(finalAiGenerated.pricingBreakdown)
		};
	}

	const statusRaw = String(p.status ?? '');
	const moderationRaw = (p.moderation_status as string) || 'visible';
	const numericId = Number(p.id);
	const ownerId = Number(p.user_id);

	return {
		id: `db-${numericId}`,
		databaseId: numericId,
		seekerUserId: Number.isFinite(ownerId) ? String(ownerId) : String(p.user_id ?? ''),
		hasClientInvite: Number(p.has_client_invite) === 1,
		hasLinkedSeeker: Number(p.has_linked_seeker) === 1,
		title: (p.title as string) || '',
		city: (p.location as string) || '',
		contactEmail: (p.contact_email as string) || '',
		styleKey: (p.style as PublicPlan['styleKey']) || 'modern',
		notes: p.description as string | undefined,
		photoBeforeUrl: p.before_image_url as string | undefined,
		status: statusRaw === 'published' ? 'active' : statusRaw === 'draft' ? 'draft' : 'closed',
		createdAt: p.created_at as string,
		questionnaire,
		aiGenerated: finalAiGenerated,
		moderationStatus: moderationRaw as PublicPlan['moderationStatus'],
		share_visibility: (p.share_visibility as ShareVisibility | undefined) ?? undefined
	};
}
